/**
 * ApplicationStatusWidget — Customer-facing progress bar
 * Epic 3B: Shows 8-phase progress, current stage, broker name, "what's next"
 */

import { useState, useEffect } from 'react'
import { CheckCircle, Circle, Search, Lightbulb, FileText, Send, ClipboardList, CheckCircle2, Home, Flag, PauseCircle } from 'lucide-react'
import {
  CUSTOMER_PHASES, getPhaseIndex, STAGE_DISPLAY, getPhaseColor,
} from '../constants/applicationStages'
import { getCustomerApplication, type CustomerApplication } from '../api/brokerApi'

const PHASE_ICONS: Record<string, React.ReactNode> = {
  search: <Search className="w-4 h-4" />,
  lightbulb: <Lightbulb className="w-4 h-4" />,
  'file-text': <FileText className="w-4 h-4" />,
  send: <Send className="w-4 h-4" />,
  clipboard: <ClipboardList className="w-4 h-4" />,
  'check-circle': <CheckCircle2 className="w-4 h-4" />,
  home: <Home className="w-4 h-4" />,
  flag: <Flag className="w-4 h-4" />,
}

const WHATS_NEXT: Record<string, string> = {
  discovery: 'Your broker will schedule an initial consultation to understand your needs.',
  research: 'Your broker is researching the best loan options for you.',
  docs: 'Please gather and submit the required documents to your broker.',
  submission: 'Your application has been submitted. The lender is reviewing it.',
  assessment: 'The lender is assessing your application and may request additional info.',
  approval: 'Great news — your application is progressing through approval stages!',
  settlement: 'Almost there! Settlement is being arranged for your new home.',
  closed: 'Your application has been finalised.',
}

interface Props {
  token: string
}

export default function ApplicationStatusWidget({ token }: Props) {
  const [app, setApp] = useState<CustomerApplication | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  useEffect(() => {
    let mounted = true
    setLoading(true)
    getCustomerApplication(token)
      .then((data) => { if (mounted) setApp(data) })
      .catch(() => { if (mounted) setError(true) })
      .finally(() => { if (mounted) setLoading(false) })
    return () => { mounted = false }
  }, [token])

  if (loading) return null
  if (error || !app || !app.id) return null

  const isOnHold = app.stage === 'on_hold'
  const isSettled = app.outcome === 'approved' || app.outcome === 'settled' || app.stage === 'settled'
  const isClosed = !app.is_active && !isSettled  // declined, withdrawn, lapsed
  const currentPhaseIdx = isSettled ? CUSTOMER_PHASES.length : getPhaseIndex(app.stage)
  const phaseColor = getPhaseColor(app.stage)
  const currentPhase = currentPhaseIdx >= 0 ? CUSTOMER_PHASES[currentPhaseIdx] : null

  const formatCurrency = (v: number | null) =>
    v ? new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 0 }).format(v) : null

  return (
    <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-5">
        <h3 className="text-lg font-bold text-gray-900">Application Progress</h3>
        {isOnHold && (
          <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
            On Hold
          </span>
        )}
        {!isOnHold && app.is_active && (
          <span className="text-xs font-medium px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: phaseColor }}>
            {STAGE_DISPLAY[app.stage] || app.stage}
          </span>
        )}
        {!app.is_active && app.outcome && (
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
            app.outcome === 'settled' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
          }`}>
            {app.outcome.charAt(0).toUpperCase() + app.outcome.slice(1)}
          </span>
        )}
      </div>

      {/* On Hold Banner */}
      {isOnHold && (
        <div className="mb-5 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
          <PauseCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-900">Your application is currently on hold</p>
            <p className="text-sm text-amber-700 mt-1">
              Your broker has paused your application. This is normal and can happen for a number of reasons.
              They'll be in touch to let you know when things are moving again.
            </p>
            {app.broker_name && (
              <p className="text-sm text-amber-700 mt-2">
                Contact your broker <span className="font-medium">{app.broker_name}</span> if you have any questions.
              </p>
            )}
          </div>
        </div>
      )}

      {/* Settled Banner */}
      {isSettled && (
        <div className="mb-5 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-green-900">Your loan has settled!</p>
            <p className="text-sm text-green-700 mt-1">
              Congratulations — your application has been finalised and settlement is complete.
            </p>
          </div>
        </div>
      )}

      {/* Declined / Withdrawn / Lapsed Banner */}
      {isClosed && (
        <div className="mb-5 p-4 bg-gray-50 border border-gray-200 rounded-lg flex items-start gap-3">
          <Circle className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-gray-900">
              Application {app.outcome === 'declined' ? 'declined' : app.outcome === 'withdrawn' ? 'withdrawn' : 'closed'}
            </p>
            <p className="text-sm text-gray-600 mt-1">
              {app.outcome === 'declined'
                ? 'Unfortunately your application was not approved. Contact your broker to discuss alternative options.'
                : app.outcome === 'withdrawn'
                ? 'This application has been withdrawn. You can start a new application with a broker at any time.'
                : 'This application is no longer active.'}
            </p>
            {app.broker_name && (
              <p className="text-sm text-gray-600 mt-2">
                Contact <span className="font-medium">{app.broker_name}</span> if you have any questions.
              </p>
            )}
          </div>
        </div>
      )}

      {/* Phase Progress Bar — separated into bar row + icon row for alignment */}
      <div className={`mb-5 ${isOnHold || isClosed ? 'opacity-40' : ''}`}>
        {/* Progress bars */}
        <div className="flex gap-1.5 mb-3">
          {CUSTOMER_PHASES.map((phase, i) => {
            const isComplete = i < currentPhaseIdx
            const isCurrent = i === currentPhaseIdx
            return (
              <div
                key={phase.id}
                className={`flex-1 h-2.5 rounded-full ${
                  isComplete ? 'bg-green-500' : isCurrent ? '' : 'bg-gray-200'
                }`}
                style={isCurrent ? { backgroundColor: phaseColor } : undefined}
              />
            )
          })}
        </div>
        {/* Icons + labels */}
        <div className="flex">
          {CUSTOMER_PHASES.map((phase, i) => {
            const isComplete = i < currentPhaseIdx
            const isCurrent = i === currentPhaseIdx
            return (
              <div key={phase.id} className="flex-1 flex flex-col items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                  isComplete ? 'bg-green-100 text-green-600'
                    : isCurrent ? 'text-white' : 'bg-gray-100 text-gray-400'
                }`}
                  style={isCurrent ? { backgroundColor: phaseColor } : undefined}
                >
                  {isComplete ? <CheckCircle className="w-4 h-4" /> : (PHASE_ICONS[phase.icon] || <Circle className="w-4 h-4" />)}
                </div>
                <span className={`text-[11px] mt-1 text-center leading-tight ${
                  isCurrent ? 'font-bold text-gray-900' : 'text-gray-500'
                }`}>
                  {phase.label}
                </span>
              </div>
            )
          })}
        </div>
      </div>

      {/* Details */}
      <div className="space-y-2 text-sm">
        {app.lender_name && (
          <p className="text-gray-600">
            <span className="font-medium text-gray-900">Lender:</span> {app.lender_name}
          </p>
        )}
        {app.loan_amount && (
          <p className="text-gray-600">
            <span className="font-medium text-gray-900">Loan:</span> {formatCurrency(app.loan_amount)}
          </p>
        )}
        {app.broker_name && (
          <p className="text-gray-600">
            <span className="font-medium text-gray-900">Broker:</span> {app.broker_name}
            {app.business_name && ` — ${app.business_name}`}
          </p>
        )}
        {app.settlement_date && (
          <p className="text-gray-600">
            <span className="font-medium text-gray-900">Settlement:</span> {new Date(app.settlement_date).toLocaleDateString()}
          </p>
        )}
      </div>

      {/* What's Next — skip for On Hold (already shown above) */}
      {currentPhase && app.is_active && !isOnHold && (
        <div className="mt-4 p-3 bg-blue-50 rounded-lg">
          <p className="text-xs font-medium text-blue-800 mb-1">What's Next</p>
          <p className="text-sm text-blue-700">{WHATS_NEXT[currentPhase.id] || 'Your broker will be in touch soon.'}</p>
        </div>
      )}
    </div>
  )
}
